import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-security';

/**
 * Security and compliance highlights card for SignNow product pages.
 * Single green background card with a heading on top and a 2-column grid of checkmark items below.
 * Renders each item as a bold inline title followed by descriptive text, unlike plain-list user-profile cards.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for security and data protection for {keyword}. 6-10 words.
 * @example
 *   <sn-security
 *     items='[{"title":"256-bit AES Encryption","text":"All documents and data are encrypted at rest and in transit."},{"title":"SOC 2 Type II Certified","text":"Independently audited security controls you can trust."},{"title":"HIPAA Compliant","text":"Healthcare-grade data protection for sensitive documents."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Security & Data Protection</me-heading>
 *   </sn-security>
 */
@Component({
  tag: 'sn-security',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcSecurityFeatures {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Security feature items. Each item can be a plain string or an object with title and text.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Security feature label. 2-5 words.
   * @item.property {string} text - Concise security capability value or fact. 4-10 words.
   * @item.content title text
   * @required items
   * @minItems 6
   * @maxItems 6
   */
  @Prop({ mutable: true }) items?: (string | { title?: string; text?: string })[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {array} items - Fallback items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        raw_html?: string;
        items?: (string | { title?: string; text?: string })[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <slot name="heading">
            {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
          </slot>
          {items.length > 0 && (
            <div class={`${COMPONENT_NAME}__grid`}>
              {items.map((item) => {
                const isObj = typeof item === 'object';
                return (
                  <div class={`${COMPONENT_NAME}__item`}>
                    <me-icon name="interface--check" class={`${COMPONENT_NAME}__check`} width="24" height="24" lazy={false}></me-icon>
                    <div class={`${COMPONENT_NAME}__item-content`}>
                      {isObj ? ([
                        item.title && <me-heading tag="h3" class={`${COMPONENT_NAME}__item-title`}>{item.title}:</me-heading>,
                        item.text && <me-text tag="span"> {item.text}</me-text>,
                      ]) : (
                        <me-text tag="span">{item as string}</me-text>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </me-section>
    );
  }
}
